import {
  AdminImageCropInput,
  AdminImageInput,
  AdminInput,
  AdminPhoneInput,
  AdminRadioInput,
  AdminSwitch,
  Button,
  Div,
  ErrorToast,
  H3,
  H5,
  Label,
  Modal,
  P,
  PropsModal,
  SubTitleWithTooltipIcon,
  SuccessToast,
  createBrandTemplate,
  lightTheme,
  updateBrandTemplate,
  updateBrandTemplatePhone,
  reportError,
} from '@vgrubs/components';
import {
  BrandTemplateEntity,
  FoodType,
} from 'components/src/types/brand.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

const typeOfFoodOptions = [
  { name: 'Breakfast', value: FoodType.breakfast },
  { name: 'Lunch', value: FoodType.lunch },
  { name: 'Dinner', value: FoodType.dinner },
];

export const BrandTemplateAdd = ({
  modalIsOpen,
  closeModal,
  setLoading,
  refetch,
  type = 'add',
}: PropsModal & {
  modalIsOpen: BrandTemplateEntity | null | boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  refetch: () => void;
  type: 'add' | 'edit';
}) => {
  const data = modalIsOpen as BrandTemplateEntity | null;

  const [syncModal, setSyncModal] = useState(false);

  const [brandName, setBrandName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [image, setImage] = useState<string | File | Blob | null>(null);
  const [qrCode, setQrCode] = useState<string | File | Blob | null>(null);
  const [heroImage, setHeroImage] = useState<string | File | Blob | null>(null);
  const [menuPdfExample, setMenuPdfExample] = useState<
    string | File | Blob | null
  >(null);
  const [isMostPopular, setIsMostPopular] = useState(false);
  const [isVisibleToRestaurantOwners, setIsVisibleToRestaurantOwners] =
    useState(false);

  const [alreadyHasMenuPdfExample, setAlreadyHasMenuPdfExample] =
    useState(false);

  const [message, setMessage] = useState('');

  const [typeOfFood, setTypeOfFood] = useState<FoodType[]>([]);

  const isEdit = type === 'edit';
  const isAdd = type === 'add';

  useEffect(() => {
    if (data && isEdit && typeof data === 'object') {
      setBrandName(data.name);
      setPhoneNumber(data.phoneNumber ?? '');
      setImage(data.imageUrl ?? '');
      setQrCode(data.printingImageUrl ?? '');
      setMessage(data.printingMessage ?? '');
      if (data.menuPdfExampleUrl) {
        setMenuPdfExample(data.menuPdfExampleUrl ?? null);
        setAlreadyHasMenuPdfExample(true);
      }
      setIsMostPopular(data.mostPopular ?? false);
      setIsVisibleToRestaurantOwners(data.visible ?? false);
      setTypeOfFood(data.foodTypes ?? []);
      setHeroImage(data.heroImageUrl ?? '');
    } else {
      setBrandName('');
      setPhoneNumber('');
      setImage('');
      setQrCode('');
      setMessage('');
      setMenuPdfExample(null);
      setIsMostPopular(false);
      setIsVisibleToRestaurantOwners(false);
      setTypeOfFood([]);
      setHeroImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen]);

  async function handleAddEdit(syncWithBrands?: boolean) {
    const template = {
      name: brandName,
      printingMessage: message,
      foodTypes: typeOfFood,
      mostPopular: isMostPopular,
      visible: isVisibleToRestaurantOwners,
    } as Partial<BrandTemplateEntity> & {
      syncWithBrands?: boolean;
    };

    if (isAdd) {
      delete template.visible;
    }

    if (isEdit) {
      template.phoneNumber = phoneNumber;
      template.syncWithBrands = syncWithBrands;
    }

    const formData = new FormData();
    formData.append('template', JSON.stringify(template));

    if (typeof image !== 'string' && image !== null) {
      formData.append('image', image);
    }

    if (typeof qrCode !== 'string' && qrCode !== null) {
      formData.append('printingImage', qrCode);
    }

    if (typeof heroImage !== 'string' && heroImage !== null) {
      formData.append('heroImage', heroImage);
    }

    if (typeof menuPdfExample !== 'string' && menuPdfExample !== null) {
      formData.append('menuPdfExample', menuPdfExample);
    }

    if (menuPdfExample === null && alreadyHasMenuPdfExample) {
      formData.append('removePdfExample', JSON.stringify(true));
    }

    try {
      setLoading(true);

      if (isAdd) {
        const { data } = await createBrandTemplate(formData);
        await updateBrandTemplatePhone(data.data.id, phoneNumber);
        refetch();
        SuccessToast(data.message);
      } else {
        if (!data) return;
        if (typeof data !== 'object') return;
        const res = await updateBrandTemplate(data.id, formData);
        setSyncModal(false);
        refetch();
        SuccessToast(res.data.message);
      }

      setLoading(false);
      closeModal?.();
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!brandName) return ErrorToast('Please enter Brand Name');
    if (!phoneNumber) return ErrorToast('Please enter Phone Number');
    if (!image) return ErrorToast('Please enter Brand Image');
    if (!typeOfFood.length)
      return ErrorToast('Please select at least one type of food');
    // Qr code is optional
    if (isEdit) {
      setSyncModal(true);
    } else {
      handleAddEdit();
    }
  }

  const handleFoodTypeMultiSelect = (value: FoodType) => {
    if (typeOfFood.includes(value)) {
      setTypeOfFood((prev) => prev.filter((type) => type !== value));
    } else {
      setTypeOfFood((prev) => [...prev, value]);
    }
  };

  return (
    <>
      <Modal
        isOpen={!!modalIsOpen}
        closeModal={closeModal}
        logo={false}
        header={`${isEdit ? 'Edit' : 'Add'} Brand`}
        width="800px"
      >
        <form onSubmit={handleSubmit}>
          <Div $flex $gap="24px" $columnSM $mb="24">
            <AdminInput
              label="Brand Name"
              placeholder="Enter here"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              $textCenter
            />
            <AdminPhoneInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              label="Phone"
              $textCenter
            />
          </Div>

          <Label label="Type of Food (Multi-Select)" />

          <Div $flex $gap="16px" $gapSM="8px" $mb="24" $columnSM>
            {typeOfFoodOptions.map((option) => (
              <Div $box $p="4px 16px" $width="33%" $widthSM="100%">
                <AdminRadioInput
                  bold
                  checked={typeOfFood.includes(option.value)}
                  label={option.name}
                  onClick={() => handleFoodTypeMultiSelect(option.value)}
                />
              </Div>
            ))}
          </Div>

          <Div $mb="24" $gap="24px" $flex $columnSM>
            <Div $width="50%" $widthSM="100%">
              <AdminImageInput
                image={image}
                onChange={(img) => setImage(img)}
                label="Brand Image"
                height="356px"
                heightSM="150px"
                // onDelete={() => setImage(null)}
              />
            </Div>
            <Div $width="50%" $widthSM="100%" $gap="24px" $flex $column>
              <AdminImageCropInput
                image={heroImage}
                onChange={(img) => setHeroImage(img)}
                label="Hero Image"
                tooltip="This image will appear as the hero image on the exported PDF menu."
                height="150px"
                heightSM="150px"
                // onDelete={() => setHeroImage(null)}
              />
              <AdminImageInput
                image={qrCode}
                onChange={(img) => setQrCode(img)}
                label="Footer Image"
                height="150px"
                heightSM="150px"
                tooltip="Upload a QR code that will appear on the receipt, directing customers to order online."
                // onDelete={() => setQrCode(null)}
              />
            </Div>
          </Div>

          <Div $mb="24">
            <AdminInput
              label="Menu PDF Example"
              value={
                menuPdfExample
                  ? typeof menuPdfExample === 'string'
                    ? menuPdfExample
                    : URL.createObjectURL(menuPdfExample as unknown as Blob)
                  : ''
              }
              $textCenter
              type="file"
              onChange={(e) => setMenuPdfExample(e.target.files?.[0] ?? null)}
              onXButtonClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuPdfExample(null);
              }}
            />
            {/* {menuPdfExample &&
              (typeof menuPdfExample === 'string' ||
                menuPdfExample instanceof File ||
                menuPdfExample instanceof Blob) && (
                <Disclaimer $underline $pointer $mt="8">
                  <a
                    href={
                      typeof menuPdfExample === 'string'
                        ? menuPdfExample
                        : URL.createObjectURL(menuPdfExample)
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See current uploaded file here
                  </a>
                </Disclaimer>
              )} */}
          </Div>

          <Div
            $flex
            $alignEnd
            $columnSM
            $width="100%"
            $alignStartSM
            $between
            $mt="40"
            $mtSM="30"
            $gap="24px"
          >
            <Div>
              <Div $flex $alignCenter $gap="10px" $mb="8">
                <AdminSwitch
                  checked={isMostPopular}
                  onChange={() => setIsMostPopular(!isMostPopular)}
                />
                <H5 $bold $mb="4">
                  Appear as Most Popular
                </H5>
              </Div>
              {!isAdd && (
                <Div $flex $alignCenter $gap="10px">
                  <AdminSwitch
                    checked={isVisibleToRestaurantOwners}
                    onChange={() =>
                      setIsVisibleToRestaurantOwners(
                        !isVisibleToRestaurantOwners
                      )
                    }
                    disabled={!data?.isMenuSet}
                  />

                  <SubTitleWithTooltipIcon
                    mb="4"
                    text="Visible to Restaurant Owners"
                    tooltip={
                      !data?.isMenuSet
                        ? 'This setting is only available if the menu is set for this brand template.'
                        : undefined
                    }
                  />
                </Div>
              )}
            </Div>

            <Button $selected type="submit">
              Submit
            </Button>
          </Div>
        </form>
      </Modal>
      <Modal
        isOpen={syncModal}
        closeModal={() => {
          setSyncModal(false);
        }}
        logo={false}
        zIndex={110}
      >
        <H3 $mb="10" $color={lightTheme.primary}>
          Sync
        </H3>
        <P $mb="24">Sync this setting across all stores with this brand?</P>

        <Div $flex $gap="16px">
          <Button $selected $w="100%" onClick={() => handleAddEdit(true)}>
            Yes
          </Button>
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => handleAddEdit(false)}
          >
            No
          </Button>
        </Div>
      </Modal>
    </>
  );
};
